/**
 * runMultiAgentContextCompress — multi-agent parity for dev-agent's
 * `runDevAgentContextCompress`.
 *
 * Mirrors the dev-agent technique:
 *   • token estimate = chars / 3.5 over the conversation since the last compress
 *     (dev-agent windows after a "🗜️ Context compressed" feed marker; the
 *     multi-agent thread has no marker rows, so we window with a module-level
 *     baseline reset on each compress / session change).
 *   • compress = send a `[SYSTEM:COMPRESS]` run on the active session. The
 *     backend fast-path summarizes prior turns via session_memory/result
 *     compaction and emits a context_compressed signal, freeing context.
 *
 * Used by the composer Compress button (token meter + click action).
 */
import toast from "react-hot-toast";
import { useMultiAgentStore } from "@/stores/multiAgentStore";

export const MIN_TOKENS_TO_COMPRESS = 30_000;

const COMPRESS_INTENT =
  "[SYSTEM:COMPRESS] Summarize the conversation so far and free up context.";

// Conversation chars at the last compression — the meter shows the live window
// since then (mirrors dev-agent's post-marker window). Reset on session change.
let compressedBaselineChars = 0;
let baselineSessionId: string | null = null;

function totalConversationChars(): number {
  const s = useMultiAgentStore.getState();
  let chars = 0;
  for (const t of s.thread) {
    chars += (t.intent || "").length + (t.finalMarkdown || "").length;
  }
  const activeIntent = s.chat.find((m) => m.role === "user")?.text || "";
  chars += activeIntent.length + (s.finalMarkdown || "").length;
  return chars;
}

/** Approx context tokens (chars/3.5) since the last compression — dev-agent scale. */
export function computeMultiAgentContextTokens(): number {
  const sid = useMultiAgentStore.getState().sessionId;
  if (sid !== baselineSessionId) {
    compressedBaselineChars = 0;
    baselineSessionId = sid;
  }
  const live = Math.max(0, totalConversationChars() - compressedBaselineChars);
  return Math.round(live / 3.5);
}

/**
 * Trigger a context-compression turn on the active session. No-op (with a
 * toast) when the conversation is already small. Shared by the composer's
 * token control / Compress button.
 */
export async function runMultiAgentContextCompress(): Promise<void> {
  const tokens = computeMultiAgentContextTokens();
  if (tokens < MIN_TOKENS_TO_COMPRESS) {
    toast("Context is small — no compression needed");
    return;
  }
  const store = useMultiAgentStore.getState();
  const sessionId = store.sessionId;
  if (!sessionId) {
    toast.error("No active session");
    return;
  }
  const inFlight =
    !store.readOnly &&
    (store.status === "running" || store.status === "starting" ||
      store.status === "planning" || store.status === "awaiting_approval");
  if (inFlight) {
    toast("Wait for the current run to finish");
    return;
  }
  toast("Compressing context…");
  try {
    if (store.runId) store.pushTurnToThread();
    store.reset();
    const { createRun } = await import("@/services/multi-agent");
    const created = await createRun(COMPRESS_INTENT, sessionId);
    store.startRun(created.run_id, "🗜️ Compress context");
    // Restart the live window — the meter drops until new turns accrue.
    compressedBaselineChars = totalConversationChars();
    baselineSessionId = sessionId;
  } catch {
    toast.error("Compression failed");
  }
}
